import * as React from 'react';
import { SyntheticEvent, useState } from 'react';

// Components
import {
  Dropdown,
  DropdownItemProps,
  Grid,
  GridColumn,
  GridRow,
  Search
} from 'semantic-ui-react';
import { Button } from '@120wateraudit/waterworks';
import { Modal } from '@120wateraudit/envirio-components';
import styled from 'styled-components';
import { SearchResult, toastError, toastSuccess } from 'src/components/shared';

// Custom Hooks
import {
  useGetUserAccountsQuery,
  useAddAccountToUserMutation,
  useGetRolesQuery
} from 'src/services';

interface Props {
  user: any;
  toggleAddingToAccount: () => void;
  addingToAccount: boolean;
}

const AddAccountModal = ({
  user,
  toggleAddingToAccount,
  addingToAccount
}: Props) => {
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const { data: responseAccounts } = useGetUserAccountsQuery({
    userId: user.id,
    paramsData: {
      pageNumber: 1,
      pageSize: 10,
      searchTerm,
      excludeFromUser: true
    }
  });
  const { data: roles } = useGetRolesQuery();
  const [roleIds, setRoleIds] = useState<number[] | undefined>(undefined);
  const [addAccount] = useAddAccountToUserMutation();
  const [accountId, setAccountId] = useState<number | undefined>(undefined);
  const [accountType, setAccountType] = useState<number | undefined>(undefined);
  const onSubmitAddAccount = async () => {
    if (accountId && user && roleIds) {
      await addAccount({
        userId: user.id,
        accountId,
        roleIds
      })
        .unwrap()
        .then(() => toastSuccess('Successfully added account to this user.'))
        .catch(() => toastError('Add account failed, please try again.'));
      toggleAddingToAccount();
      setSearchTerm(undefined);
      setAccountId(undefined);
      setAccountType(undefined);
      setRoleIds(undefined);
    } else {
      toastError('Please fill both User and Role required fields');
    }
  };

  const onSelectAccount = async (
    event: SyntheticEvent,
    data: { result: { id: number; title: string; accountType: number } }
  ) => {
    setSearchTerm(data.result.title);
    setAccountId(data.result.id);
    setAccountType(data.result.accountType);
  };

  const accounts = responseAccounts?.items || [];
  const accountsRecord = accounts.map(account => ({
    id: account.id,
    title: account.name,
    description: account.id.toString(),
    accountType: account.accountType
  }));

  const convertRolesToItemProps = (): DropdownItemProps[] => {
    const itemprops = [{ text: '', value: 0, key: 0 }];
    return itemprops.concat(
      roles
        ? roles
            .filter(
              r =>
                (r.accountType === accountType || r.accountType === 0) &&
                r.name !== 'PWS Hydra View'
            )
            .map(r => {
              return {
                text: r.name,
                value: r.id,
                key: r.id
              };
            })
        : []
    );
  };

  return (
    <Modal
      content={
        <Grid>
          <GridRow columns={1}>
            <GridColumn>
              <h4>Add Account To User</h4>
            </GridColumn>
          </GridRow>
          <GridRow columns={1}>
            <GridColumn>
              <SearchWrapper>
                <Search
                  input={{ icon: 'search', iconPosition: 'left' }}
                  fluid={true}
                  noResultsMessage="No Accounts Found"
                  placeholder="Search for an account"
                  onBlur={() => {
                    if (!searchTerm) {
                      // If we end up without a search term we assume the user is clearing the search
                      setSearchTerm(undefined);
                      setAccountId(undefined);
                      setRoleIds(undefined);
                    }
                  }}
                  onFocus={(e: SyntheticEvent) =>
                    (e.target as HTMLInputElement).select()
                  }
                  onResultSelect={onSelectAccount}
                  onSearchChange={(_, data) => setSearchTerm(data.value)}
                  resultRenderer={SearchResult}
                  results={accountsRecord}
                  value={searchTerm}
                />
              </SearchWrapper>
            </GridColumn>
          </GridRow>
          <GridRow columns={1}>
            <GridColumn>
              <Dropdown
                placeholder="Select Role"
                fluid
                multiple={true}
                selection={true}
                onChange={(_, data) => {
                  setRoleIds(data.value as number[]);
                }}
                value={roleIds}
                options={convertRolesToItemProps()}
              />
            </GridColumn>
          </GridRow>
          <GridRow>
            <GridColumn>
              <div
                style={{
                  marginTop: '20px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                <Button
                  onClick={() => {
                    setSearchTerm('');
                    setRoleIds(undefined);
                    toggleAddingToAccount();
                  }}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={() => onSubmitAddAccount()}>
                  Add
                </Button>
              </div>
            </GridColumn>
          </GridRow>
        </Grid>
      }
      isOpen={addingToAccount}
      overflowStyle="visible"
      toggle={toggleAddingToAccount}
    />
  );
};

const SearchWrapper = styled.div`
  width: 80%;
  &&& {
    .ui.input {
      width: 100%;
    }
  }
`;

export default AddAccountModal;
